import { createContext, useContext, useEffect, useState } from 'react'
import RoutesManagment from '../Routes/RoutesManagment';
import { logIn, ResetPassword, UsersForChangePassword, ChangePassword } from '../Services/AuthenticationService';
import { CartProvider } from './CartProvider';
import { CompanyInfoProvider } from './CompanyInfoProvider';
import { FavoriteItemsProvider } from './FavoriteProductsProvider';
import LoadingSpinner from '../Components/Components/LoadingSpinner';
import useHandleAuthState from '../Hooks/useHandleAuthState';
import { CustomerProvider } from './CustomerProvider';
import { useConfig } from './ConfigProvider';
import { LastWatchedProductsProvider } from './LastWatchedProductsProvider';
import { InvoicesProvider } from './InvoicesProvider';
import { UpdateUserMarketing } from '../Services/UserService';
import { OffersProvider } from './OffersProvider';
import { SavedBasketsProvider } from './SavedBasketsProvider';
import { GetHeaders } from '../Services/GetHeadersToAxios';
import { UserProvider } from './UserProvider';
import { useParams } from './ParamsProvider';

const AuthContext = createContext()

export const useAuth = () => {
    return useContext(AuthContext)
}

const AuthProvider = ({ children }) => {
    const { api, logOutTrigger } = useConfig()
    const { authState, pending, setAuthState, setRefresh } = useHandleAuthState()
    const { autoLogOutTime } = useParams()
    const [wholesalePrices, setWholesalePrices] = useState(!!authState.user)
    const [loginError, setLoginError] = useState("")

    useEffect(() => {
        setWholesalePrices(!!authState.user)
    }, [authState.user])

    useEffect(() => { //trigger na wylogowanie gdy wygaśnie token
        if (logOutTrigger !== null && authState.user) {
            logout(false, true)
        }
    }, [logOutTrigger])

    useEffect(() => { //timer na automatycznne odświeżenie tokena   
        if (!authState.user || !api) {
            return
        }
        const interval = setInterval(async () => {
            try {
                const response = await api.post("/api/v1/Authentication/refreshToken", {}, { headers: GetHeaders() })
                if (response.data) {
                    localStorage.setItem("token", response.data)
                }
            } catch (err) {
                var accountIsNoLongerActive = true
                if (err.response.status != 401) {
                    console.error("Błąd przy odświeżaniu tokena:", err)
                    accountIsNoLongerActive = false
                }
                logout(true, true, accountIsNoLongerActive)
            }
        }, autoLogOutTime * 60 * 1000)

        return () => clearInterval(interval)
    }, [authState.user])

    // Po poprawnym logowaniu zapisujemy token i przeładowujemy stronę
    const login = async (data, goToHomePage = true) => {
        setLoginError("")
        try {
            const res = await logIn(api, data)
            if (res.data.errorMessage && res.data.errorMessage.trim() !== '') {
                return { manyCompanies: false, companies: null, errorMessage: res.data.errorMessage }
            }
            if (res.data.companies && res.data.companies.length > 0) {
                return { manyCompanies: true, companies: res.data.companies, errorMessage: res.data.errorMessage }
            }
            else {
                localStorage.clear()
                sessionStorage.clear()
                localStorage.setItem('token', res.data.token)
                // Po ustawieniu tokena użytkownik zostanie pobrany w useHandleAuthState przy kolejnym renderze
                if (goToHomePage) {
                    window.location.href = '/'
                }
                return { manyCompanies: false, companies: null, errorMessage: res.data.errorMessage }
            }
        } catch (e) {
            console.error(e)
            if (e.response && e.response.status === 401) {
                setLoginError(e.response.data)
            } else {
                setLoginError("Błąd połączenia z serwerem")
            }
        }
    }

    const updateConsentForUser = (consentMarketing, email, sms, phone) => {
        UpdateUserMarketing(api, consentMarketing, email, sms, phone)
            .then(() => {
                setRefresh(true)
            })
            .catch(e => {
                console.error(e)
            })
    }

    const logout = (goToLoginPage = true, tokenExpired = false) => {
        const path = window.location.pathname.toLowerCase()
        const search = window.location.search.toLowerCase()
        if (tokenExpired) {
            const noAutoLogoutPaths = [
                "/confirmaccount",
                "/accountcreated",
                "/changepassword"
            ]

            // sprawdzenie: czy jesteśmy na /cart i jednocześnie w query jest custId? Aby blokowac wylogowanie na realizacji oferty ale na Cart już wylogować
            const isCartWithCustId = path === "/cart" && new URLSearchParams(search).has("custId".toLowerCase())
            const cancelAutoLogout = noAutoLogoutPaths.includes(path) || isCartWithCustId

            if (!cancelAutoLogout) {
                // localStorage.setItem("shouldClearAuth", "true")
                window.location.href = "/login?autoLogout"
            }
            return
        }
        if (goToLoginPage) {
            window.location.href = "/login"
        }
    }

    const logOutClearDataFuncBody = () => {
        localStorage.clear()
        sessionStorage.clear()
        setAuthState({
            user: null,
            token: null,
            custId: null,
            siteId: null,
            whId: null,
            currency: 'PLN',
            selectedCustomer: null,
            selectedCustomerTax: null,
            selectedCustomerData: null
        })
    }

    const changeSelectedCustomer = (
        custId,
        siteId,
        whId,
        selectedCustomer,
        currency,
        selectedCustomerTax,
        zipCode,
        city,
        street,
        houseNum,
        localNum,
        email,
        phone,
        dpl
    ) => {
        setAuthState(prev => ({
            ...prev,
            custId,
            siteId,
            whId,
            currency: currency || prev.currency,
            selectedCustomer,
            selectedCustomerTax,
            selectedCustomerData: {
                name: selectedCustomer,
                zipCode,
                city,
                street,
                houseNum,
                localNum,
                email,
                phone,
                tax: selectedCustomerTax,
                whId,
                custId,
                siteId,
                dpl
            }
        }))
    }

    const handleSelectCustomer = (customer) => {
        setAuthState(prev => ({
            ...prev,
            selectedCustomerObject: customer
        }))
    }

    const changeSiteIdAndWhId = (siteId, whId) => {
        setAuthState(prev => ({
            ...prev,
            siteId,
            whId
        }))
    }
    const handleResetPassword = async (email) => {
        try {
            const res = await ResetPassword(api, email)
            return res.data
        } catch (error) {
            console.log(error)
            return { resetType: 0, message: 'Wewnętrzny błąd aplikacji!' }
        }
    }
    const handleCheckChangePasswordLink = async (link) => {
        try {
            const res = await UsersForChangePassword(api, link)
            return res.data
        } catch (error) {
            console.log(error)
            return null
        }
    }
    const handleChangePasswordFromLink = async (link, custId, password, marketingAgree, marketingAgreeEmail, marketingAgreePhone, marketingAgreeSms) => {
        try {
            const res = await ChangePassword(api, link, custId, password, marketingAgree, marketingAgreeEmail, marketingAgreePhone, marketingAgreeSms)
            return { result: true, errorMessage: '' }
        } catch (error) {
            console.log(error)
            return { result: false, errorMessage: error.response.data }
        }
    }

    const handleAdmitEInvoiceAgreement = (invoiceEmail) => {
        setAuthState(prev => ({
            ...prev,
            user: {
                ...prev.user,
                customerInfo: prev.user.customerInfo.map((item, index) =>
                    index === 0 ? { ...item, isEInvoiceConsent: true, eInvoiceEmail: invoiceEmail } : item
                )
            }
        }))
    }

    if (pending)
        return (
            <div className='row vh-100 align-items-center' style={{ overflow: 'none', backgroundColor: 'white' }}>
                <LoadingSpinner />
            </div>
        )

    return (
        <AuthContext.Provider value={{
            authState,
            login,
            logout,
            loginError,
            setLoginError,
            wholesalePrices,
            setWholesalePrices,
            changeSelectedCustomer,
            changeSiteIdAndWhId,
            updateConsentForUser,
            handleResetPassword,
            handleCheckChangePasswordLink,
            handleChangePasswordFromLink,
            logOutClearDataFuncBody,
            handleAdmitEInvoiceAgreement,
            handleSelectCustomer,
        }}>
            <CompanyInfoProvider>
                <LastWatchedProductsProvider>
                    <SavedBasketsProvider>
                        <FavoriteItemsProvider>
                            <CartProvider>
                                <CustomerProvider>
                                    <UserProvider>
                                        <InvoicesProvider>
                                            <OffersProvider>
                                                <RoutesManagment />
                                            </OffersProvider>
                                        </InvoicesProvider>
                                    </UserProvider>
                                </CustomerProvider>
                            </CartProvider>
                        </FavoriteItemsProvider>
                    </SavedBasketsProvider>
                </LastWatchedProductsProvider>
            </CompanyInfoProvider>
        </AuthContext.Provider>
    )
}

export default AuthProvider

